/**
 * 料金グループ見出しの共通コンポーネント。
 *
 * 「女性料金」と「男性料金」で見出しの配置ロジックを分けず、同じ構造で描画するための共通部品。
 * Version 107 の男性料金グループ見出し（Grid 3列固定 → PC は flex）をマスターとして、
 * 女性・男性とも以下のレイアウトルールを完全に共有する。
 *
 *   ・英字ラベル／日本語見出し／右側ライン の3領域
 *   ・スマホ：grid-cols-[126px_auto_1fr]（英字列を固定幅にして日本語見出しの開始X座標を揃える）
 *   ・PC（md以上）：md:flex md:gap-4（従来どおりの横並び1行）
 *   ・文字は nowrap・flex-shrink-0、右ラインのみ min-w-0 flex-1 で伸縮
 *
 * 男女で異なるのは「表示テキスト」と「配色（tone）」のみ。
 */

type PriceGroupHeadingProps = {
  /** 英字ラベル（例：LADIES / MENS / FACE・NECK） */
  en: string;
  /** 日本語見出し（例：女性 / 男性（顔・首）） */
  title: string;
  /** 配色トーン。light＝女性料金（明るい背景×濃色文字）／ dark＝男性料金（チャコール背景×ウォームホワイト文字） */
  tone?: "light" | "dark";
};

export default function PriceGroupHeading({ en, title, tone = "light" }: PriceGroupHeadingProps) {
  const isDark = tone === "dark";

  return (
    <div className="grid grid-cols-[126px_auto_1fr] items-baseline gap-2 md:flex md:gap-4">
      {/* 英字ラベル：固定幅カラム。スマホは字間のみ0.2emへ詰め、PCは従来の0.3em。 */}
      <span
        className={`en-eyebrow flex-shrink-0 whitespace-nowrap !tracking-[0.2em] text-[10px] md:!tracking-[0.3em] md:text-[11px] ${
          isDark ? "text-accent-400" : "text-secondary-700"
        }`}
      >
        {en}
      </span>
      {/* 日本語見出し：2列目。開始X座標は男女・グループで共通（1列目が固定幅のため）。 */}
      <span
        className={`flex-shrink-0 whitespace-nowrap font-heading text-[15px] md:text-lg ${
          isDark ? "text-background-50" : "text-foreground-950"
        }`}
      >
        {title}
      </span>
      {/* 右側ライン：残り幅をすべて消費（余裕がなければここが短くなる）。高さ・色はトーンに応じて。 */}
      <span className={`h-px min-w-0 flex-1 ${isDark ? "bg-background-50/20" : "bg-foreground-950/10"}`} />
    </div>
  );
}